import { useState, useEffect, useCallback, useRef } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { FaEdit } from "react-icons/fa";
import { createBlog, getBlogDetails, getBlogBySlug, updateBlog, imageUpload } from "../../Apis/blogs";
import "../../assets/css/admin.css";

export default function BlogModal({ onClose, blogId, blogSlug }) {
  const [form, setForm] = useState({
    title: "",
    short_description: "",
    description: "",
    content: "",
    image: null,
    slug: "",
    meta_title: "",
    meta_description: "",
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const editorRef = useRef(null); // Ref for CKEditor container

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      const file = files[0];
      setForm((prev) => ({
        ...prev,
        [name]: file,
        [`${name}Preview`]: file ? URL.createObjectURL(file) : null,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setForm((prev) => ({ ...prev, content: data }));
  };

  const fetchSingleBlog = useCallback(async () => {
    try {
      let res;
      if (blogId) {
        res = await getBlogDetails(blogId);
      } else if (blogSlug) {
        res = await getBlogBySlug(blogSlug);
      }
      
      if (res?.data?.success && res?.data?.data) {
        setForm({
          ...res.data.data,
          imagePreview: res.data.data.image_path,
        });
      }
    } catch (error) {
      console.log(error, "error");
    }
  }, [blogId, blogSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("short_description", form.short_description);
    formData.append("description", form.description);
    formData.append("content", form.content);
    formData.append("slug", form.slug);
    formData.append("meta_title", form.meta_title);
    formData.append("meta_description", form.meta_description);

    if (form.image) {
      formData.append("image", form.image);
    }

    setLoading(true);
    try {
      if (blogId) {
        await updateBlog(blogId, formData);
      } else {
        await createBlog(formData);
      }
      onClose();
    } catch (error) {
      console.log(error, "error");
      if (error.response?.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError(error.response?.data?.message || "Failed to save blog. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSingleBlog();
  }, [fetchSingleBlog]);

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal-container">
        {/* Modal Header */}
        <div className="admin-modal-header">
          {(blogId || blogSlug) ? "Edit Blog Post" : "Create New Blog"}
        </div>

        {/* Modal Body */}
        <div className="admin-modal-body">
          {error && (
            <div className="admin-error-message">
              {error}
            </div>
          )}
          <form
            id="blog-form"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div className="admin-modal-section">
              <label className="admin-modal-label">Title</label>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                className="admin-modal-input"
                placeholder="Enter blog title"
                required
              />
            </div>

            <div className="admin-modal-section">
              <label className="admin-modal-label">Slug</label>
              <input
                type="text"
                name="slug"
                value={form.slug}
                onChange={handleChange}
                className="admin-modal-input"
                placeholder="example-title"
                required
              />
            </div>

            <div className="admin-modal-section">
              <label className="admin-modal-label">Meta Title</label>
              <input
                type="text"
                name="meta_title"
                value={form.meta_title}
                onChange={handleChange}
                className="admin-modal-input"
                placeholder="Enter meta title for SEO"
              />
            </div>

            <div className="admin-modal-section">
              <label className="admin-modal-label">Meta Description</label>
              <textarea
                name="meta_description"
                value={form.meta_description}
                onChange={handleChange}
                className="admin-modal-textarea"
                rows={3}
                placeholder="Enter meta description for SEO"
              />
            </div>

            <div className="admin-modal-section">
              <label className="admin-modal-label">Short Description</label>
              <textarea
                name="short_description"
                value={form.short_description}
                onChange={handleChange}
                className="admin-modal-textarea"
                rows={3}
                placeholder="Brief summary..."
              />
            </div>

            <div className="admin-modal-section">
              <label className="admin-modal-label">Content</label>
              <div
                ref={editorRef}
                className="ckeditor-container"
              >
                <CKEditor
                  editor={ClassicEditor}
                  data={form.content}
                  onChange={handleEditorChange}
                  config={{
                    extraPlugins: [
                      function CustomUploadAdapterPlugin(editor) {
                        editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
                          return {
                            upload: async () => {
                              const file = await loader.file;
                              const formData = new FormData();
                              formData.append("upload", file);

                              try {
                                const res = await imageUpload(formData);
                                return {
                                  default: res.data.url,
                                };
                              } catch (err) {
                                console.error("CKEditor image upload failed", err);
                                throw err;
                              }
                            },
                            abort: () => { },
                          };
                        };
                      },
                    ],

                    toolbar: [
                      "heading",
                      "|",
                      "bold",
                      "italic",
                      "underline",
                      "strikethrough",
                      "subscript",
                      "superscript",
                      "|",
                      "fontFamily",
                      "fontSize",
                      "fontColor",
                      "fontBackgroundColor",
                      "|",
                      "alignment",
                      "blockQuote",
                      "|",
                      "bulletedList",
                      "numberedList",
                      "todoList",
                      "|",
                      "link",
                      "imageUpload",
                      "mediaEmbed",
                      "|",
                      "insertTable",
                      "tableColumn",
                      "tableRow",
                      "mergeTableCells",
                      "|",
                      "code",
                      "codeBlock",
                      "horizontalLine",
                      "pageBreak",
                      "|",
                      "undo",
                      "redo",
                      "|",
                      "removeFormat",
                      "specialCharacters",
                      "htmlEmbed",
                      "|",
                      "findAndReplace",
                      "selectAll",
                    ],
                    image: {
                      toolbar: [
                        "imageStyle:full",
                        "imageStyle:side",
                        "imageTextAlternative",
                        "|",
                        "imageUpload",
                      ],
                    },
                  }}
                />
              </div>
            </div>

            <div className="admin-modal-section">
              <label className="admin-modal-label">
                {(blogId || blogSlug) ? "Change Image" : "Upload Image"}
              </label>
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleChange}
                className="admin-modal-input"
              />

              {form.imagePreview && (
                <img
                  src={form.imagePreview}
                  alt="Preview"
                  className="admin-modal-image-preview"
                  style={{ width: "10rem", height: "10rem", objectFit: "cover" }}
                />
              )}
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="admin-modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="admin-modal-btn admin-modal-btn-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="blog-form"
            className="admin-modal-btn admin-modal-btn-submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                {(blogId || blogSlug) ? "Updating..." : "Publishing..."}
              </>
            ) : (
              (blogId || blogSlug) ? (
                <>
                  <FaEdit /> Update
                </>
              ) : (
                "Publish"
              )
            )}
          </button>
        </div>
      </div>
    </div>
  );
}